import type { components } from '@connected/api-client/api.generated';

type ElectricalInspectionKind = components['schemas']['ElectricalInspectionKind'];

/**
 * Configuration for allowed inspection report combinations.
 *
 * This configuration defines which inspection kinds can be combined together.
 * Each entry represents a valid combination of inspection kinds.
 *
 * Current allowed combinations:
 * - Scope 8 PI & Scope 10
 * - Scope 10 & Scope 12
 *
 * To add new combinations, simply add a new entry to the ALLOWED_COMBINATIONS array.
 * To remove a combination, remove the corresponding entry.
 */
export const ALLOWED_COMBINATIONS: readonly ElectricalInspectionKind[][] = [
    ['Scope8', 'Scope10'],
    ['Scope10', 'Scope12'],
] as const;

/**
 * Inspection kinds that can participate in combinations.
 * Derived from ALLOWED_COMBINATIONS for consistency.
 */
export const COMBINABLE_INSPECTION_KINDS = new Set<ElectricalInspectionKind>(
    ALLOWED_COMBINATIONS.flat(),
);

/**
 * Get all inspection kinds that can be combined with a given inspection kind.
 */
export function getCompatibleKinds(kind: ElectricalInspectionKind): Set<ElectricalInspectionKind> {
    const compatible = new Set<ElectricalInspectionKind>();

    for (const combination of ALLOWED_COMBINATIONS) {
        if (combination.includes(kind)) {
            for (const combinedKind of combination) {
                if (combinedKind !== kind) {
                    compatible.add(combinedKind);
                }
            }
        }
    }

    return compatible;
}

/**
 * Check if a combination of inspection kinds is valid.
 * A combination is valid if:
 * - It has 0 or 1 items (no restriction needed)
 * - All items together form a subset of one of the allowed combinations
 */
export function isValidCombination(kinds: ElectricalInspectionKind[]): boolean {
    if (kinds.length <= 1) {
        return true;
    }

    // Check if the selected kinds match one of the allowed combinations
    for (const allowedCombination of ALLOWED_COMBINATIONS) {
        const isValid = kinds.every(kind => allowedCombination.includes(kind));
        if (isValid) {
            return true;
        }
    }

    return false;
}

/**
 * Get the inspection kinds that should be disabled based on current selection.
 * Returns a set of inspection kinds that cannot be selected.
 */
export function getDisabledKinds(
    selectedKinds: ElectricalInspectionKind[],
    allKinds: ElectricalInspectionKind[],
): Set<ElectricalInspectionKind> {
    const disabled = new Set<ElectricalInspectionKind>();

    // If nothing is selected, nothing is disabled
    if (selectedKinds.length === 0) {
        return disabled;
    }

    // Get the intersection of compatible kinds for all selected items
    let compatibleWithAll: Set<ElectricalInspectionKind> | null = null;

    for (const selectedKind of selectedKinds) {
        const compatible = getCompatibleKinds(selectedKind);

        if (compatibleWithAll === null) {
            compatibleWithAll = compatible;
        }
        else {
            // Intersect with the current compatible set
            compatibleWithAll = new Set<ElectricalInspectionKind>(
                [...compatibleWithAll].filter((kind: ElectricalInspectionKind) => compatible.has(kind)),
            );
        }
    }

    // Mark all kinds as disabled except:
    // - Already selected kinds
    // - Kinds that are compatible with all selected kinds
    for (const kind of allKinds) {
        if (selectedKinds.includes(kind)) {
            continue; // Already selected, not disabled
        }

        if (compatibleWithAll && compatibleWithAll.has(kind)) {
            continue; // Compatible with all selected, not disabled
        }

        disabled.add(kind);
    }

    return disabled;
}
